/**
 * 认证相关接口
 */
import md5 from 'md5';
import { apiGet, apiPost, apiPut, apiRequest } from '@/utils/request';

/** 菜单项（来自 /api/auth/info 的 menus 字段） */
export interface MenuItem {
  id: number;
  parent_id: number;
  name: string;
  key: string;
  /**
   * 1=父级分组（无 component），2=叶子页面（有 component），3=权限节点。
   * 权限节点按挂载位置区分：挂在权限组（如 cus.data.permissions）下为数据权限，
   * 挂在菜单节点下为按钮权限（见 userStore.hasBtnPermission）
   */
  type: 1 | 2 | 3;
  path: string;
  icon: string;
  component: string | null;
  children: MenuItem[];
  action: unknown;
  sort: number;
  status: number;
  system: number;
}

/** 登录请求：Body 为 multipart/form-data。密码登录传 username、password；手机登录可传 type、username、phone、code */
export interface AuthLoginPayload {
  username?: string;
  password?: string;
  type?: string;
  phone?: string;
  code?: string;
}

/** 登录响应 data 中的 user */
export interface AuthLoginUser {
  account_id: number;
  app_id: number;
  app_code: string;
  username: string;
  nickname: string | null;
  role_name: string;
  role_code: string;
  /** 腾讯 IM SDKAppID */
  im_app_id?: number;
  /** 腾讯 IM userId（坐席端形如 agent_{id}） */
  im_identifier?: string;
  /** 腾讯 IM UserSig */
  im_user_sign?: string;
}

/** 登录响应 data 中的 permission 项 */
export interface AuthLoginPermission {
  key: string;
  name: string;
  type: number;
  path: string | null;
  parent_id: number;
}

/** 登录成功后的 data 结构 */
export interface AuthLoginResponse {
  token: string;
  user: AuthLoginUser;
  permissions: AuthLoginPermission[];
  menus: MenuItem[];
}

/** /api/auth/info 响应 data 结构 */
export interface AuthInfoResponse {
  agent_id?: number;
  app_id?: number;
  app_code?: string;
  username?: string;
  nickname?: string;
  role_name?: string;
  role_code?: string;
  /** 0=离线 1=在线 2=隐身 */
  is_online?: 0 | 1 | 2;
  phone?: string;
  /** 公海消息声音提醒：未分配给自己的会话来新消息时是否响，0=关 1=开 */
  public_msg_sound?: 0 | 1;
  /** 个人消息声音提醒：分配给自己的会话来新消息时是否响，0=关 1=开 */
  personal_msg_sound?: 0 | 1;
  /** 系统提醒弹窗自动消失秒数 */
  notify_popup_seconds?: number;
  /** 客户是否可见意向车型实体：1=可见 0=不可见，决定商家信息是否展示「校准车型」 */
  vehicle_model_visible?: 0 | 1;
  /** 是否允许查看客户手机号/微信号明文：1=允许 0=不允许 */
  can_view_plain_pii?: 0 | 1;
  /** 访客未发言自动结束会话小时数，实时对话默认展示该时长内的会话 */
  visitor_inactive_hours?: number;
  permissions?: string[];
  menus?: MenuItem[];
}

/** 获取当前登录用户信息 GET /api/auth/info */
export async function queryAuthInfo(): Promise<AuthInfoResponse> {
  return apiGet<AuthInfoResponse>('/auth/info');
}

/** 登录：POST /api/auth/login，Body multipart/form-data，返回 data 含 token、user、permissions、menus */
export async function queryAuthLogin(payload: AuthLoginPayload): Promise<AuthLoginResponse> {
  const form = new FormData();
  (['username', 'password', 'type', 'phone', 'code'] as const).forEach((key) => {
    let v = payload[key];
    if (v != null && v !== '') {
      if (key === 'password') v = md5(v);
      form.append(key, String(v));
    }
  });
  return apiRequest<AuthLoginResponse>('/auth/login', { method: 'POST', data: form });
}

/**
 * 技术支持一键登录：用 admin 端下发的票据换登录态。
 *
 * 公开接口无需登录态，成功响应与 /auth/login 完全一致（token / user / permissions / menus）。
 * 票据 10 秒过期且只能用一次，拿到后必须立即调用，中间不能插入确认交互。
 */
export async function queryAuthLoginByTicket(ticket: string): Promise<AuthLoginResponse> {
  return apiPost<AuthLoginResponse>('/auth/login-by-ticket', { ticket });
}

/** 发送登录验证码（坐席账号+手机号） */
export async function queryAuthSendCode(payload: Record<string, string>) {
  return apiPost('/auth/send-code', payload);
}

/** 登出，使服务端会话失效 */
export async function queryAuthLogout() {
  return apiRequest('/auth/logout', { method: 'POST' });
}

/** 在线状态：0=离线 1=在线 2=隐身 */
export type OnlineStatusValue = 0 | 1 | 2;

/** 默认状态：0=清除默认 1=默认在线 2=默认隐身；不传则不修改 */
export type DefaultStatusValue = 0 | 1 | 2;

/** 更新在线状态请求体 */
export interface AuthUpdateOnlineStatusPayload {
  /** 当前在线状态，必填 */
  is_online: OnlineStatusValue;
  /** 默认状态，不传则不修改 */
  default_status?: DefaultStatusValue;
}

/** 更新在线状态，PUT /api/auth/online-status */
export function queryAuthUpdateOnlineStatus(payload: AuthUpdateOnlineStatusPayload) {
  return apiPut('/auth/online-status', payload);
}

/** 刷新 IM 签名响应 */
export interface RefreshImSignResponse {
  im_app_id: number;
  im_identifier: string;
  im_user_sign: string;
}

/** 刷新坐席 IM 签名（业务码 70001 触发），POST /api/auth/refresh-im-sign */
export function queryRefreshImSign(): Promise<RefreshImSignResponse> {
  return apiPost<RefreshImSignResponse>('/auth/refresh-im-sign');
}

/** 坐席查看目标：按渠道账号，或按访客用户（后端反查其渠道账号） */
export type AgentViewingTarget = { account_id: number } | { user_id: number };

/** 坐席查看绑定的响应：bindings 为本坐席当前全部绑定及各自的引用计数 */
export interface AgentViewingResponse {
  agent_id: number;
  action: 'bind' | 'unbind' | 'clear';
  account_id?: number;
  bindings: { account_id: number; ref_count: number }[];
}

/** 绑定坐席正在查看的目标，后端引用计数 +1，便于 IM 推送消息定向 */
export function queryAgentViewingBind(target: AgentViewingTarget) {
  return apiRequest<AgentViewingResponse>('/agent/viewing', {
    method: 'POST',
    data: { action: 'bind', ...target },
    silent: true,
  });
}

/** 解绑坐席正在查看的目标，后端引用计数 -1，归零才真正解绑；keepalive 用于页面卸载时发出 */
export function queryAgentViewingUnbind(target: AgentViewingTarget, keepalive = false) {
  return apiRequest<AgentViewingResponse>('/agent/viewing', {
    method: 'POST',
    data: { action: 'unbind', ...target },
    silent: true,
    keepalive,
  });
}

/** 清空当前坐席的全部查看绑定（所有标签页的绑定一并清掉） */
export function queryAgentViewingClear() {
  return apiRequest<AgentViewingResponse>('/agent/viewing', {
    method: 'POST',
    data: { action: 'clear' },
    silent: true,
  });
}
